import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackgroundBubbles from '../components/BackgroundBubbles';
import MoodSelector from '../components/BookRecommender/MoodSelector';
import BookSearch from '../components/BookRecommender/BookSearch';
import PopularBooks from '../components/PopularBooks';
import Navbar from '../components/Navbar';
import { similarBooksRules } from '../rules';
import { API_BASE_URL } from '../config/api';

function HomePage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('mood');
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Track similar books feature usage
  const trackSimilarBooksUsage = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/analytics/similar-books-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
    } catch (error) {
      console.error('Error tracking similar books usage:', error);
      // Don't show error to user, just log it
    }
  };

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
    navigate('/recommendations', { 
      state: { 
        mode: 'mood',
        mood: mood
      }
    });
  };

  const handleBookSelect = (book) => {
    setSelectedBooks(prev => [...prev, book]);
  };

  const handleBookRemove = (bookId) => {
    setSelectedBooks(prev => prev.filter(book => book.id !== bookId));
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'similar') {
      trackSimilarBooksUsage();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedBooks.length === 0) return;

    // Add client-side validation for maximum books
    if (selectedBooks.length > similarBooksRules.maxBooks) {
      setError(similarBooksRules.errors.tooManyBooks);
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Check API usage for all users (authenticated and non-authenticated)
      const usageResponse = await fetch(`${API_BASE_URL}/api/usage/similar-books`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        
        // If user has reached the limit, show paywall directly
        if (usageData.remaining <= 0) {
          setIsLoading(false);
          navigate('/recommendations', { 
            state: { 
              mode: 'similar',
              books: selectedBooks,
              recommendations: [],
              limitReached: true
            }
          });
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'similar',
          input: selectedBooks.map(book => ({
            title: book.title,
            author: book.author
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // If limit reached error, navigate to recommendations with limitReached flag
        if (errorData.limitReached) {
          setIsLoading(false);
          navigate('/recommendations', { 
            state: { 
              mode: 'similar',
              books: selectedBooks,
              recommendations: [],
              limitReached: true
            }
          });
          return;
        }
        
        throw new Error(errorData.message || 'Failed to get recommendations');
      }

      const recommendations = await response.json();
      setIsLoading(false);
      
      navigate('/recommendations', { 
        state: { 
          mode: 'similar',
          books: selectedBooks,
          recommendations
        }
      });
    } catch (error) {
      console.error('Error getting recommendations:', error);
      setError(error.message || 'Failed to get recommendations. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-100/80 via-white to-blue-100/80 -z-20" />

      <Navbar showBackButton={false} />

      <BackgroundBubbles />

      <div className="relative z-10">
        <main className="container mx-auto pb-24 pt-20">
          <div className="w-full">
            {/* Hero Section */}
            <div className="relative py-4 text-center w-full max-w-3xl mx-auto">
              {/* Hero Background */}
              <div className="absolute inset-0 -z-10">
                {/* Cloud-like blurry background */}
                <div className="absolute inset-0 bg-white/40 blur-3xl rounded-[50%]"></div>
                <div className="absolute inset-0 bg-white/60 blur-2xl scale-95 rounded-[60%]"></div>
                <div className="absolute inset-0 bg-white/80 blur-xl scale-90"></div>
                
                {/* Additional glow effects */}
                <div className="absolute -inset-x-20 -inset-y-10 bg-white/20 blur-2xl"></div>
                <div className="absolute inset-0 bg-gradient-radial from-white/90 via-white/50 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 space-y-2">
                <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-fraunces">
                  Your next favorite book
                  <br />
                  awaits discovery.
                </h1>
                <p className="pt-2 sm:pt-4 text-base sm:text-xl text-gray-600 font-space px-6 sm:px-0 mx-auto max-w-[380px] sm:max-w-none">
                  Get personalized recommendations based on your mood or favorite books.
                </p>
              </div>
            </div>

            {/* Search Form */}
            <div className="relative w-full max-w-2xl mx-auto mt-8 sm:mt-16">
              <form onSubmit={handleSubmit} className="space-y-8 px-4">
                {/* Toggle Buttons */}
                <div className="flex justify-center">
                  <div className="inline-flex p-1 bg-gray-100/80 backdrop-blur-sm rounded-full">
                    <button
                      type="button"
                      onClick={() => handleModeChange('mood')}
                      className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                        mode === 'mood'
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      By Mood
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeChange('similar')}
                      className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                        mode === 'similar'
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      By Similar Books
                    </button>
                  </div>
                </div>

                {/* Mood Selector or Book Search */}
                <div className="space-y-6">
                  {mode === 'mood' && (
                    <>
                      <p className="text-gray-600 font-medium text-center">
                        How are you feeling today?
                      </p>
                      <div className="px-4 sm:px-0">
                        <MoodSelector selectedMood={selectedMood} onMoodSelect={handleMoodSelect} />
                      </div>
                    </>
                  )}
                  
                  {mode === 'similar' && (
                    <BookSearch 
                      selectedBooks={selectedBooks}
                      onBookSelect={handleBookSelect}
                      onBookRemove={handleBookRemove}
                      mode="similar"
                    />
                  )}
                </div>

                {error && (
                  <div className="text-red-500 text-sm text-center">{error}</div>
                )}

                {/* Only show submit button in 'similar' mode */}
                {mode === 'similar' && selectedBooks.length > 0 && (
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow flex items-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Finding similar books...</span>
                        </>
                      ) : (
                        'Get Recommendations'
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Popular Books Section */}
          <section className="mt-16 sm:mt-24 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 font-fraunces text-center">
                Fresh Finds & Rising Stars
              </h2>
              <PopularBooks />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default HomePage; 